import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { recordUserActivity } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';

const SESSION_KEY = 'activity_session_id';

const getSessionId = () => {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, next);
    return next;
};

const ActivityTracker: React.FC = () => {
    const location = useLocation();
    const { user } = useAuth();
    const lastEventRef = useRef<Record<string, number>>({});
    const sessionIdRef = useRef<string>(getSessionId());

    const sendActivity = (payload: {
        actionType: string;
        pagePath?: string;
        targetType?: string;
        targetLabel?: string;
        metadata?: Record<string, any>;
    }) => {
        if (!user) return;
        void recordUserActivity({
            ...payload,
            sessionId: sessionIdRef.current,
            occurredAt: new Date().toISOString(),
        }).catch(() => {
            // best-effort logging only
        });
    };

    useEffect(() => {
        if (!user) return;
        sendActivity({
            actionType: 'page_view',
            pagePath: `${location.pathname}${location.search}`,
            targetType: 'route',
            targetLabel: location.pathname,
        });
    }, [location.pathname, location.search, user]);

    useEffect(() => {
        if (!user) return;

        const onClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;

            const interactive = target.closest('a,button,[role="button"],canvas,input,select,[data-activity-label]') as HTMLElement | null;
            if (!interactive) return;

            const targetType = interactive.tagName.toLowerCase();
            const text = (interactive.getAttribute('data-activity-label') || interactive.innerText || interactive.getAttribute('aria-label') || '').trim();
            const targetLabel = text.slice(0, 120) || targetType;
            const signature = `${targetType}:${targetLabel}:${location.pathname}`;
            const now = Date.now();

            if (lastEventRef.current[signature] && now - lastEventRef.current[signature] < 700) {
                return;
            }
            lastEventRef.current[signature] = now;

            sendActivity({
                actionType: 'click',
                pagePath: `${location.pathname}${location.search}`,
                targetType,
                targetLabel,
                metadata: {
                    id: interactive.id || undefined,
                    className: interactive.className || undefined,
                },
            });
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [location.pathname, location.search, user]);

    return null;
};

export default ActivityTracker;
